import React, { useState, useEffect } from 'react';
import { format, addDays, startOfWeek } from 'date-fns';
import { comprehensiveHRService } from '../../../lib/checkInAttendanceService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShiftScheduling = ({ shifts, hrProfiles, loading, onRefresh, gymId, managerId }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [conflictCheck, setConflictCheck] = useState(null);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const getShiftsForDate = (date) => {
    return shifts?.filter(
      (shift) => format(new Date(shift?.schedule_date), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success';
      case 'in_progress':
        return 'bg-primary/10 text-primary';
      case 'scheduled':
        return 'bg-warning/10 text-warning';
      case 'cancelled':
        return 'bg-error/10 text-error';
      case 'no_show':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleCreateShift = async (shiftData) => {
    const hrProfile = hrProfiles?.find((p) => p?.staff_id === shiftData?.staffId);
    if (!hrProfile) return;

    // Check for conflicts
    const { data: hasConflict } = await comprehensiveHRService?.checkShiftConflict(
      shiftData?.staffId,
      shiftData?.scheduleDate,
      shiftData?.shiftStartTime,
      shiftData?.shiftEndTime
    );

    if (hasConflict) {
      setConflictCheck('Shift conflict detected! This staff member already has a shift at this time.');
      return;
    }

    const { error } = await comprehensiveHRService?.createShift({
      gymId,
      staffId: shiftData?.staffId,
      hrProfileId: hrProfile?.id,
      scheduleDate: shiftData?.scheduleDate,
      shiftStartTime: shiftData?.shiftStartTime,
      shiftEndTime: shiftData?.shiftEndTime,
      position: shiftData?.position,
      location: shiftData?.location,
      assignedBy: managerId
    });

    if (!error) {
      setShowCreateModal(false);
      setConflictCheck(null);
      onRefresh?.();
    }
  };

  const handleUpdateShiftStatus = async (shiftId, newStatus) => {
    const { error } = await comprehensiveHRService?.updateShift(shiftId, { status: newStatus });
    if (!error) {
      onRefresh?.();
    }
  };

  const totalShifts = shifts?.length || 0;
  const activeShifts = shifts?.filter((s) => s?.status === 'in_progress')?.length || 0;
  const scheduledShifts = shifts?.filter((s) => s?.status === 'scheduled')?.length || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="Calendar" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{totalShifts}</div>
              <div className="text-xs text-muted-foreground">Total Shifts</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-success/10">
              <Icon name="CheckCircle" size={24} className="text-success" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{activeShifts}</div>
              <div className="text-xs text-muted-foreground">Active Now</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-warning/10">
              <Icon name="Clock" size={24} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{scheduledShifts}</div>
              <div className="text-xs text-muted-foreground">Scheduled</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="Users" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{hrProfiles?.length || 0}</div>
              <div className="text-xs text-muted-foreground">Staff Members</div>
            </div>
          </div>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setWeekStart(addDays(weekStart, -7))}
              variant="outline"
              size="sm"
            >
              <Icon name="ChevronLeft" size={18} />
            </Button>
            <h2 className="text-xl font-semibold text-foreground">
              Week of {format(weekStart, 'MMM d, yyyy')}
            </h2>
            <Button
              onClick={() => setWeekStart(addDays(weekStart, 7))}
              variant="outline"
              size="sm"
            >
              <Icon name="ChevronRight" size={18} />
            </Button>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Icon name="Plus" size={18} />
            <span>Create Shift</span>
          </Button>
        </div>
      </div>

      {/* Weekly Calendar View */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        {weekDays?.map((day) => {
          const dayShifts = getShiftsForDate(day);
          const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

          return (
            <div
              key={day?.toISOString()}
              className={`card ${
                isToday ? 'ring-2 ring-primary' : ''
              }`}
            >
              <div className="mb-4 pb-3 border-b border-border">
                <div className="text-sm font-medium text-muted-foreground">
                  {format(day, 'EEE')}
                </div>
                <div className="text-2xl font-bold text-foreground">
                  {format(day, 'd')}
                </div>
              </div>

              <div className="space-y-2">
                {dayShifts?.length === 0 ? (
                  <div className="text-center py-4 text-sm text-muted-foreground">
                    No shifts
                  </div>
                ) : (
                  dayShifts?.map((shift) => (
                    <div
                      key={shift?.id}
                      className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="text-sm font-medium text-foreground truncate">
                          {shift?.staff?.full_name}
                        </div>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            getStatusColor(shift?.status)
                          }`}
                        >
                          {shift?.status}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {shift?.shift_start_time} - {shift?.shift_end_time}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {shift?.position}
                      </div>
                      {shift?.status === 'scheduled' && (
                        <div className="mt-2 flex gap-1">
                          <button
                            onClick={() => handleUpdateShiftStatus(shift?.id, 'in_progress')}
                            className="text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20"
                          >
                            Start
                          </button>
                          <button
                            onClick={() => handleUpdateShiftStatus(shift?.id, 'cancelled')}
                            className="text-xs px-2 py-1 rounded bg-error/10 text-error hover:bg-error/20"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Shift Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">Create New Shift</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setConflictCheck(null);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {conflictCheck && (
              <div className="mb-4 p-3 rounded-lg bg-error/10 border border-error/20">
                <div className="flex items-start gap-2">
                  <Icon name="AlertCircle" size={18} className="text-error mt-0.5" />
                  <p className="text-sm text-error">{conflictCheck}</p>
                </div>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e?.preventDefault();
                const formData = new FormData(e?.target);
                handleCreateShift({
                  staffId: formData?.get('staffId'),
                  scheduleDate: formData?.get('scheduleDate'),
                  shiftStartTime: formData?.get('shiftStartTime'),
                  shiftEndTime: formData?.get('shiftEndTime'),
                  position: formData?.get('position'),
                  location: formData?.get('location')
                });
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Staff Member *
                </label>
                <select
                  name="staffId"
                  required
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select staff member</option>
                  {hrProfiles?.map((profile) => (
                    <option key={profile?.staff_id} value={profile?.staff_id}>
                      {profile?.staff?.full_name} - {profile?.position}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Date *
                </label>
                <input
                  type="date"
                  name="scheduleDate"
                  required
                  defaultValue={format(selectedDate, 'yyyy-MM-dd')}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Start Time *
                  </label>
                  <input
                    type="time"
                    name="shiftStartTime"
                    required
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    End Time *
                  </label>
                  <input
                    type="time"
                    name="shiftEndTime"
                    required
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Position *
                </label>
                <input
                  type="text"
                  name="position"
                  required
                  placeholder="e.g., Front Desk, Trainer, Cleaner"
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  placeholder="e.g., Main Gym, Studio A"
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1">
                  Create Shift
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setConflictCheck(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftScheduling;